// Document registers. Mirrors lambda_handlers/api/registers/ and registers/views.py.
// See docs/Y2026-Q4/design-document-registers.md.
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { queryKeys } from "../queryKeys";
import { useAgentsHttp } from "../useAgentsHttp";

export type RegisterStatus = "building" | "ready" | "failed";

/** Where a line stands on a project. Must agree with registers/store.py. */
export type LineStatus =
  | "unassessed"
  | "meets"
  | "deviates"
  | "not_applicable"
  | "not_evidenced"
  | "not_due"
  | "deferred"
  | "recheck"
  /** Read, never written: another option of the same choice meets. */
  | "met_by_option";

/** Why a requirement is not assessed by its agents; null when it is. Must agree with
 * registers/store.py. */
export type ClauseStanding =
  | "restatement"
  | "dependency"
  | "term"
  | "mechanism"
  | "boilerplate"
  | "principle"
  | "conflict"
  | "awaiting_document"
  | "member"
  | "criterion";

/** A list member's state on its list's assessment. */
export type ItemState = "provided" | "not_applicable" | "missing" | "deficient";

/** A document filed in one role, in one scope: a project's own, or a Global data scope.
 * Mirrors registers/views.py ``slot_view``. */
export interface ClauseListSlot {
  role: string;
  role_label: string;
  authority: string;
  scope: "global" | "project";
  project_id: string | null;
  document_id: string;
  document_name: string;
  filled_by_agent: string;
  filled_by_run_id: string | null;
  filled_at: string | null;
  replaced_document_id: string | null;
  replace_reason: string | null;
  carried_assessments: number;
  active: boolean;
  retired_at: string | null;
  retired_by_agent: string | null;
  retired_reason: string | null;
  retired_by_document_id: string | null;
}

export interface DocumentRegister {
  register_id: string;
  document_id: string;
  document_kind: "upload" | "global";
  document_name: string;
  project_id: string | null;
  /** A folder source's files, read in or not (drawings); empty for one file. */
  files: { id: string; name: string }[];
  version: number;
  status: RegisterStatus;
  counts: {
    lines?: number;
    requirements?: number;
    contested?: number;
    unclassified?: number;
    /** Every clause carries its shape (obligor, kind, condition, choice). */
    shaped?: boolean;
    /** Set once the whole list has been read for goals, links, forms and documents. */
    outlined?: boolean;
    outline_error?: string;
    /** Failed reads of this text; not asked again past three. */
    outline_attempts?: number;
    goals?: number;
    restatements?: number;
    conflicts?: number;
    external?: number;
    /** Pairs of similar clauses compared for contradictions, and any left uncompared. */
    pairs_asked?: number;
    pairs_error?: string;
  };
  reason: string;
  requested_by_agent: string;
  requested_by_run_id: string | null;
  requested_for_project_id: string | null;
  comment_ids: string[];
  error: string | null;
  has_trace: boolean;
  created_at: string | null;
  published_at: string | null;
  /** Its document has changed since: a newer text awaits a register. */
  stale: boolean;
  /** The role it fills (or last filled); null when never filed in one. */
  slot: ClauseListSlot | null;
}

export interface LineAssessment {
  status: LineStatus;
  /** With not_due: the assessor's own row stage the clause falls due at. */
  due_at: string | null;
  /** With not_due: the date (YYYY-MM-DD) it falls due by; either clock brings it back. */
  due_by: string | null;
  evidence: string;
  accepts_deviation: string | null;
  reason: string;
  set_by_agent: string;
  set_by_run_id: string | null;
  set_at: string | null;
  /** On a list obligation: each member's state, by its line_id. */
  items: Record<string, { state: ItemState; note: string }>;
}

/** One assessor's view of a line on a project. */
export interface AgentLineStatus {
  agent: string;
  status: LineStatus;
  moved: string[];
  assessment: LineAssessment | null;
}

/** Another clause of the same version a line points at. */
export interface LineRef {
  line_id: string;
  reference: string;
  title: string;
}

/** A definition clause whose term the line uses; its text is in the response's
 * definition_texts, keyed by line_id. */
export interface LineDefinition extends LineRef {
  term: string;
}

export interface RegisterLine {
  line_id: string;
  line_key: string;
  position: number;
  start_ref: string;
  end_ref: string;
  page: number;
  /** In a folder source: the file it is in and its page there. */
  file: { id: string; name: string; page: number } | null;
  reference: string;
  classification:
    | "requirement"
    | "definition"
    | "heading"
    | "informative"
    | "irrelevant"
    | "unclassified";
  reason: string;
  title: string;
  topics: string[];
  contested: string | null;
  text: string;
  /** False for a non-requirement, and for a restatement: assessed on its original. */
  assessable: boolean;
  /** The specialists that assess it (a restatement's: its original's). */
  assessors: string[];
  /** The headings it sits under, outermost first; empty before the first heading. */
  section_path: string[];
  /** Its innermost section's id, a key of the view's groups. */
  group_key: string;
  /** The earlier clause it restates, whose status it shows. */
  restates: LineRef | null;
  /** The later clauses that restate it. */
  restated_by: LineRef[];
  definitions: LineDefinition[];
  /** Each "Phase 2" / "Stage 4" its text or section names. */
  phases: string[];
  /** For a row of a package goal: the document's name, for a compact list. */
  document_title: string | null;
  /** Why its agents do not assess it (on a project, also awaiting_document); null for
   * one they assess and for a non-requirement. A line's status means something only
   * when this is null. */
  standing: ClauseStanding | null;
  obligor: "provider" | "purchaser" | "third_party" | "mutual" | null;
  kind:
    | "obligation"
    | "commercial_term"
    | "mechanism"
    | "boilerplate"
    | "principle"
    | null;
  /** The condition under which it applies at all, as the document states it. */
  applies_when: string | null;
  /** Options of one choice share it; the choice is met when any option is. */
  one_of: string | null;
  /** A date (YYYY-MM-DD) the clause itself sets. */
  states_due_by: string | null;
  due_rule: string | null;
  /** The obligation in one checkable sentence ("What needs to be satisfied"). */
  statement: string | null;
  /** Keys of the document-stated goals it serves. */
  goal_keys: string[];
  conflicts: (LineRef & { note: string })[];
  /** Clauses limiting the same thing, both met by meeting the stricter; ``stricter``
   * says this one is. */
  limits: (LineRef & { note: string; stricter: boolean })[];
  /** On a project: agents that took it on as ours though its shape put it off their
   * list, with their reason. */
  reclassified?: { agent: string; reason: string; set_at: string | null }[];
  /** A document it cannot be assessed without, and the role that would supply it. */
  /** Advice: a document it may need. ``fileable``: a clause list of that role could
   * supply it, so on a project it waits for one (standing awaiting_document); otherwise
   * it is assessed on what is held. */
  depends_on: {
    document: string;
    role: string | null;
    fileable: boolean;
    /** A Global data file beside the document that holds what the clause needs. */
    file_id?: string;
  } | null;
  /** The instrument not yet executed it sits in (a licence, a deed); it is still assessed. */
  form: string | null;
  /** list: one checklist entry assessed once with a state per member (its parts);
   * member: an item of the list in part_of; criterion: a check on the deliverable in
   * part_of. Null for an entry of its own. */
  entry: "list" | "member" | "criterion" | null;
  part_of: LineRef | null;
  /** A list's members, or a deliverable's criteria, in document order. */
  parts: LineRef[];
  /** A reviewer's note on its topics alone, for operators; never a dispute. */
  routing_note: string | null;
  /** The condition its whole section applies under, when its obligations share one. */
  group_condition: string | null;
  /** Present when the register is read for a project where it is in force. */
  status?: LineStatus;
  moved?: string[];
  assessment?: LineAssessment | null;
  by_agent?: AgentLineStatus[];
}

export interface ProjectRegisterLine extends RegisterLine {
  document_id: string;
  document_name: string;
  register_id: string;
  status: LineStatus;
  moved: string[];
  area: string | null;
  assessment: LineAssessment | null;
  by_agent: AgentLineStatus[];
  /** On a list: its members by their newest state from any assessor. */
  item_counts: (Record<ItemState | "unstated", number> & { total: number }) | null;
}

/** A line status, or a section's own: some line still owed a decision, or all met. */
export type GroupStatus = LineStatus | "open" | "met";

export interface GroupStatusView {
  /** deviates > not_evidenced > open > not_due > met, over the section and its subsections. */
  status: GroupStatus;
  counts: Partial<Record<LineStatus, number>>;
}

/** One section of a clause list. Counts include its subsections; restatements are not
 * counted among requirements. */
export interface RegisterGroup {
  group_key: string;
  title: string;
  section_path: string[];
  parent_key: string | null;
  /** Its first clause's position, for document order. */
  position: number;
  clauses: number;
  /** Assessable requirements: restatements are counted apart. */
  requirements: number;
  restatements: number;
  /** The condition all its obligations apply under, decided once; null when they differ. */
  condition: string | null;
}

/** A goal the document itself states (a submission that is a condition to award, the
 * certificate whose articles make COD). */
export interface RegisterGoal {
  key: string;
  title: string;
  /** package: a deliverables table or document register, its rows the members; list:
   * one obligation whose items are the members; gate: any other goal it states. */
  kind: "package" | "list" | "gate";
  /** When or for what it falls due, as the document says. */
  due: string;
  /** Words copied exactly from the clause stating it, or "". */
  quote: string;
  source: LineRef | null;
  /** Its member clauses. */
  line_ids: string[];
  /** A package's numbers its table skips that the document never states. */
  skipped_numbers: string[];
  /** A package's numbers the document states outside its members. */
  numbers_elsewhere: (LineRef & { number: string })[];
  /** The specialist whose work delivers it. */
  owner: string | null;
  owner_label: string | null;
  /** On a project: its owner's verdict and the date it serves. */
  project?: GoalOnProject;
}

export interface GoalOnProject {
  goal_id: string;
  owner: string | null;
  owner_label: string | null;
  /** The owner's verdict, recheck once a clause of it moved since, or unassessed. */
  status: string;
  /** Derived: at_risk from its milestone less its lead time, overdue past it. */
  risk: "at_risk" | "overdue" | null;
  verdict_reason: string | null;
  milestone: { date_id: string; what: string; date: string } | null;
  milestone_none: string | null;
  /** Days after (or, negative, before) the milestone the goal itself falls. */
  offset_days: number;
  /** The goal's own day: the milestone moved by its offset. */
  due: string | null;
  lead_days: number | null;
  lead_reason: string | null;
  /** The day its clauses fall due: the milestone less the lead time. */
  due_from: string | null;
  open_members: number;
  members: number;
}

export interface RegisterTopic {
  key: string;
  label: string;
  assessor: string | null;
  area: string | null;
}

export interface RegisterRole {
  key: string;
  label: string;
  description: string;
}

/** Per document in force, the requirements nobody assesses on the project. */
export interface ClauseGaps {
  unrouted: { topic: string; count: number }[];
  not_enrolled: { agent: string; count: number }[];
}

export interface RegisterView {
  register: DocumentRegister;
  versions: DocumentRegister[];
  /** Every document that has filled this document's role, newest first. */
  /** The role it fills on the project read, or null when not in force there. */
  in_force: ClauseListSlot | null;
  /** The project's enrolled specialists when the list is in force there, else empty. */
  enrolled_agents: string[];
  gaps: ClauseGaps;
  lines: RegisterLine[];
  /** Each with every assessor's roll-up on the project read, empty without one. */
  groups: (RegisterGroup & {
    status_by_agent: Record<string, GroupStatusView>;
  })[];
  goals: (RegisterGoal & {
    status_by_agent: Record<string, GroupStatusView>;
  })[];
  /** The text of every definition the lines use, by its line_id. */
  definition_texts: Record<string, string>;
  topics: RegisterTopic[];
  /** Each project where the list is in force and a specialist has assessed it. */
  assessed_on: AssessedOn[];
}

/** One specialist's clauses of a list on one project, by status. */
export interface AssessedOn {
  project_id: string;
  project_name: string;
  agent: string;
  counts: Partial<Record<LineStatus, number>>;
}

export interface ProjectRegisters {
  project_id: string;
  // The document types switched on in this project's settings; none hides the card.
  enabled_roles: string[];
  registers: DocumentRegister[];
  pending: DocumentRegister[];
  by_agent: Record<
    string,
    {
      lines: ProjectRegisterLine[];
      /** ``not_due`` counts among ``settled``. */
      coverage: {
        area: string | null;
        settled: number;
        total: number;
        not_due: number;
      }[];
      /** By document: the goals the document states that hold the agent's lines, with
       * its roll-up. */
      goals: Record<string, (RegisterGoal & GroupStatusView)[]>;
      /** By document: the sections holding the agent's lines, with its roll-up. */
      groups: Record<string, (RegisterGroup & GroupStatusView)[]>;
      /** Routed to it but not assessed by it: */
      dependencies: ProjectRegisterLine[];
      terms: ProjectRegisterLine[];
      mechanisms: ProjectRegisterLine[];
      boilerplate: ProjectRegisterLine[];
      principles: ProjectRegisterLine[];
      conflicts: ProjectRegisterLine[];
      awaiting_documents: ProjectRegisterLine[];
      /** The text of every definition its lines use, by line_id. */
      definition_texts: Record<string, string>;
    }
  >;
  topics: RegisterTopic[];
  roles: RegisterRole[];
  /** By document in force: the lists its role held before, newest first. */
  replaced: Record<string, ClauseListSlot[]>;
  /** Project lists a Global data list of the same role overrides. */
  shadowed: ClauseListSlot[];
  gaps: Record<string, ClauseGaps>;
  enrolled_agents: string[];
}

/** One file's clause list, read for the file alone: a project's assessments are on its
 * specialists' cards (``useProjectRegisters``). */
export const useRegister = (documentId: string | null, registerId: string | null) => {
  const http = useAgentsHttp();
  return useQuery<RegisterView>(
    queryKeys.register(documentId ?? "", registerId),
    () =>
      http<RegisterView>("/registers/get", {
        document_id: documentId,
        ...(registerId ? { register_id: registerId } : {}),
      }),
    {
      enabled: !!documentId,
      refetchOnWindowFocus: false,
      // A register being built publishes in a few minutes.
      refetchInterval: (data) =>
        data?.register.status === "building" ? 15_000 : false,
    },
  );
};

/** One read for every document on a page: the badge on each row. */
export const useRegistersForDocuments = (documentIds: string[]) => {
  const http = useAgentsHttp();
  const ids = [...new Set(documentIds)].sort();
  return useQuery<Record<string, DocumentRegister>>(
    queryKeys.registersForDocuments(ids.join(",")),
    async () =>
      (
        await http<{ registers: Record<string, DocumentRegister> }>(
          "/registers/for-documents",
          { document_ids: ids },
        )
      ).registers,
    { enabled: ids.length > 0, refetchOnWindowFocus: false },
  );
};

/** A project's clause lists; with ``agent``, only that specialist's lines. */
export const useProjectRegisters = (
  projectId: string | null,
  agent: string | null = null,
) => {
  const http = useAgentsHttp();
  return useQuery<ProjectRegisters>(
    queryKeys.projectRegisters(projectId ?? "", agent),
    () =>
      http<ProjectRegisters>("/registers/project", {
        project_id: projectId,
        ...(agent ? { agent } : {}),
      }),
    { enabled: !!projectId, refetchOnWindowFocus: false },
  );
};

export interface GlobalClauseList extends DocumentRegister {
  /** The Global data scope it applies by. */
  scope: { tenant: string | null; landlord: string | null };
  /** Still shared as a Global data file. */
  shared: boolean;
  /** The newest version's status, when a newer one is building or failed. */
  newest_status: RegisterStatus;
  /** The lists it replaced in its role, shown under it rather than as rows of their own. */
  replaced: ClauseListSlot[];
}

export const useGlobalRegisters = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const http = useAgentsHttp();
  return useQuery<{ documents: GlobalClauseList[]; topics: RegisterTopic[] }>(
    queryKeys.globalRegisters(),
    () => http("/registers/global", {}),
    { enabled, refetchOnWindowFocus: false },
  );
};

/** An operator's comment on a clause list, kept with its file or folder and applied to the
 * list as soon as it arrives. */
export interface ClauseComment {
  comment_id: string;
  document_id: string;
  kind: "clause" | "pair" | "list";
  clauses: { line_id: string; reference: string }[];
  quote: string;
  body: string;
  author_email: string;
  created_at: string | null;
  status: "queued" | "applying" | "applied" | "failed";
  /** The version it was made on: a comment on the output reads against it. */
  made_on_version: number | null;
  applied_version: number | null;
  applied_at: string | null;
  /** What applying it changed: a clause, or how the list reads a pair, before and after. */
  outcome: {
    before?: ClauseOutcome | string | null;
    after?: ClauseOutcome | string | null;
  } | null;
  error: string | null;
}

export interface ClauseOutcome {
  reference: string;
  classification: string;
  title: string;
  statement: string | null;
}

const IN_FLIGHT = new Set(["queued", "applying"]);

export const useClauseComments = (documentId: string) => {
  const http = useAgentsHttp();
  const client = useQueryClient();
  const inFlight = useRef(0);
  return useQuery<{ comments: ClauseComment[] }>(
    queryKeys.clauseComments(documentId),
    () => http("/registers/comments", { document_id: documentId }),
    {
      enabled: !!documentId,
      refetchOnWindowFocus: false,
      // An edit takes a few minutes; the list refreshes when it lands.
      refetchInterval: (data) =>
        data?.comments.some((c) => IN_FLIGHT.has(c.status)) ? 10_000 : false,
      onSuccess: (data) => {
        const now = data.comments.filter((c) => IN_FLIGHT.has(c.status)).length;
        // An edit landed: read the list's new version.
        if (now < inFlight.current) void client.invalidateQueries([queryKeys.register(documentId, null)[0], documentId]);
        inFlight.current = now;
      },
    },
  );
};

export const useRetryClauseComments = (documentId: string) => {
  const http = useAgentsHttp();
  const client = useQueryClient();
  return useMutation<{ queued: number; requeued: number }, Error, void>(
    () => http("/registers/comments/retry", { document_id: documentId }),
    {
      onSuccess: () => {
        void client.invalidateQueries(queryKeys.clauseComments(documentId));
      },
    },
  );
};

export const useAddClauseComment = (documentId: string) => {
  const http = useAgentsHttp();
  const { user } = useKindeAuth();
  const client = useQueryClient();
  return useMutation<
    { comment: ClauseComment; not_started?: string },
    Error,
    {
      kind: ClauseComment["kind"];
      line_ids: string[];
      body: string;
      quote?: string;
      register_id: string;
    }
  >(
    (input) =>
      http("/registers/comments/add", {
        document_id: documentId,
        actor_email: user?.email ?? "",
        ...input,
      }),
    {
      onSuccess: () => {
        void client.invalidateQueries(queryKeys.clauseComments(documentId));
      },
    },
  );
};
